import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../ThemeContext';
import LanguageSwitcher from '../LanguageSwitcher';
import ThemeSwitcher from '../ThemeSwitcher';
import GamesHistory from './GamesHistory';

const API_URL = '/api';
const TRC20_REGEX = /^T[1-9A-HJ-NP-Za-km-z]{33}$/;

const authHeaders = () => {
    const token = localStorage.getItem('token');
    return {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
    };
};

const buildStatusLabel = (t) => ({
    pending: { text: t('profile.status.pending'), className: 'text-cyber-purple glow-purple' },
    verifying: { text: t('profile.status.verifying'), className: 'text-cyber-purple glow-purple' },
    processing: { text: t('profile.status.processing'), className: 'text-cyber-purple glow-purple' },
    completed: { text: t('profile.status.completed'), className: 'text-cyber-cyan glow-cyan' },
    rejected: { text: t('profile.status.rejected'), className: 'text-cyber-pink glow-pink' },
    failed: { text: t('profile.status.failed'), className: 'text-cyber-pink glow-pink' },
    cancelled: { text: t('profile.status.cancelled'), className: 'text-cyber-dim' },
});

const formatDate = (value) => {
    if (!value) return '—';
    const d = new Date(value);
    return d.toLocaleString();
};

const shortenWallet = (wallet) => {
    if (!wallet) return '—';
    if (wallet.length <= 14) return wallet;
    return `${wallet.slice(0, 6)}...${wallet.slice(-6)}`;
};

function Profile({ player, setPlayer, onLogout }) {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { theme, setTheme, THEMES } = useTheme();

    const [loading, setLoading] = useState(true);
    const [profile, setProfile] = useState(null);
    const [walletInput, setWalletInput] = useState('');
    const [savingWallet, setSavingWallet] = useState(false);
    const [amount, setAmount] = useState('');
    const [submittingWithdrawal, setSubmittingWithdrawal] = useState(false);
    const [cancellingId, setCancellingId] = useState(null);
    const [depositAmount, setDepositAmount] = useState('');
    const [submittingDeposit, setSubmittingDeposit] = useState(false);
    const [historyTab, setHistoryTab] = useState('active');

    const loadProfile = async () => {
        try {
            const res = await fetch(`${API_URL}/profile`, { headers: authHeaders() });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(data.message || t('profile.failLoadProfile'));
            setProfile(data);
            setWalletInput(data.user.wallet || '');
            if (data.user && setPlayer) {
                setPlayer((prev) => ({ ...prev, balance: data.user.balance }));
            }
        } catch (e) {
            toast.error(e.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!localStorage.getItem('token')) {
            navigate('/');
            return;
        }
        loadProfile();
        // eslint-disable-next-line
    }, []);

    const saveWallet = async () => {
        const trimmed = walletInput.trim();
        if (!TRC20_REGEX.test(trimmed)) {
            toast.error(t('profile.invalidTrc20Short'));
            return;
        }
        setSavingWallet(true);
        try {
            const res = await fetch(`${API_URL}/profile/wallet`, {
                method: 'PUT',
                headers: authHeaders(),
                body: JSON.stringify({ wallet: trimmed }),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(data.message || t('profile.failSaveWallet'));
            setProfile((prev) => ({ ...prev, user: { ...prev.user, wallet: data.wallet } }));
            toast.success(t('profile.walletSaved'));
        } catch (e) {
            toast.error(e.message);
        } finally {
            setSavingWallet(false);
        }
    };

    const submitWithdrawal = async () => {
        const parsed = parseInt(amount);
        if (!parsed || parsed <= 0) {
            toast.error(t('profile.enterValidAmount'));
            return;
        }
        if (!profile?.user?.wallet) {
            toast.error(t('profile.saveWalletFirst'));
            return;
        }
        if (parsed < profile.minWithdrawal) {
            toast.error(t('profile.minWithdrawalToast', { amount: profile.minWithdrawal }));
            return;
        }
        if (parsed > profile.user.balance) {
            toast.error(t('profile.amountExceeds'));
            return;
        }

        setSubmittingWithdrawal(true);
        try {
            const res = await fetch(`${API_URL}/profile/withdrawals`, {
                method: 'POST',
                headers: authHeaders(),
                body: JSON.stringify({ amount: parsed }),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(data.message || t('profile.failCreateWithdrawal'));
            toast.success(t('profile.withdrawalCreated'));
            setAmount('');
            if (setPlayer) setPlayer((prev) => ({ ...prev, balance: data.balance }));
            await loadProfile();
        } catch (e) {
            toast.error(e.message);
        } finally {
            setSubmittingWithdrawal(false);
        }
    };

    const cancelWithdrawal = async (id) => {
        setCancellingId(id);
        try {
            const res = await fetch(`${API_URL}/profile/withdrawals/${id}`, {
                method: 'DELETE',
                headers: authHeaders(),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(data.message || t('profile.failCancel'));
            toast.success(t('profile.withdrawalCancelled'));
            if (setPlayer) setPlayer((prev) => ({ ...prev, balance: data.balance }));
            await loadProfile();
        } catch (e) {
            toast.error(e.message);
        } finally {
            setCancellingId(null);
        }
    };

    const submitDeposit = async () => {
        const parsed = parseFloat(depositAmount);
        if (!parsed || parsed < (profile?.minDeposit || 15)) {
            toast.error(t('profile.minDeposit', { amount: profile?.minDeposit || 15 }));
            return;
        }
        setSubmittingDeposit(true);
        try {
            const res = await fetch(`${API_URL}/profile/deposits`, {
                method: 'POST',
                headers: authHeaders(),
                body: JSON.stringify({ amount: parsed }),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(data.message || t('profile.failDeposit'));
            if (data.invoiceUrl) {
                window.open(data.invoiceUrl, '_blank');
            }
            toast.success(t('profile.depositCreated'));
            setDepositAmount('');
            await loadProfile();
        } catch (e) {
            toast.error(e.message);
        } finally {
            setSubmittingDeposit(false);
        }
    };

    if (loading || !profile) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="text-cyber-dim tracking-widest text-xs animate-pulse">{t('profile.loadingProfile')}</div>
            </div>
        );
    }

    const { user, active, history, minWithdrawal, networkFeeHint = 1, deposits = [], minDeposit = 15 } = profile;
    const hasWallet = !!user.wallet;
    const walletValid = TRC20_REGEX.test(walletInput.trim());
    const canWithdraw =
        hasWallet &&
        parseInt(amount) >= minWithdrawal &&
        parseInt(amount) <= user.balance;
    const canDeposit = parseFloat(depositAmount) >= minDeposit;

    const historyTabs = [
        { key: 'active', label: t('profile.tabs.active'), count: active.length },
        { key: 'withdrawals', label: t('profile.tabs.withdrawals'), count: history.length },
        { key: 'deposits', label: t('profile.tabs.deposits'), count: deposits.length },
        { key: 'games', label: t('profile.tabs.games') },
    ];

    return (
        <div className="min-h-screen p-2 sm:p-4 md:p-8">
            <div className="max-w-6xl mx-auto space-y-4 sm:space-y-6">
                <header className="neon-panel scanlines p-3 sm:p-5">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                        <div className="flex items-center gap-3 sm:gap-4 min-w-0">
                            <div className="text-xl sm:text-2xl font-black font-display text-white whitespace-nowrap">
                                BG<span className="text-cyber-purple glow-purple ml-1">GAMES</span>
                            </div>
                            <span className="hidden md:inline text-cyber-dim text-xs tracking-widest">{t('profile.cabinet')}</span>
                            <div className="hidden sm:flex items-center gap-2 pl-3 sm:pl-4 border-l border-cyber-border/40">
                                <span className="neon-label">{t('profile.credits')}</span>
                                <span className="text-cyber-purple glow-purple font-display text-lg sm:text-xl font-bold">
                                    {user.balance}<span className="text-cyber-dim text-xs ml-1">¢</span>
                                </span>
                            </div>
                        </div>
                        <div className="flex items-center gap-2 flex-wrap">
                            <div className="flex items-center gap-1">
                                {Object.entries(THEMES).map(([key, label]) => (
                                    <button
                                        key={key}
                                        onClick={() => setTheme(key)}
                                        className={`text-[10px] font-display uppercase tracking-widest px-2 sm:px-3 py-1 sm:py-1.5 rounded-md border transition-all duration-200 ${
                                            theme === key
                                                ? 'border-cyber-cyan/60 text-cyber-cyan bg-cyber-cyan/10'
                                                : 'border-cyber-border text-cyber-dim hover:text-cyber-text hover:border-cyber-border'
                                        }`}
                                    >
                                        {label}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                    <div className="sm:hidden flex items-center gap-2 mt-3 pt-3 border-t border-cyber-border/40">
                        <span className="neon-label">{t('profile.credits')}</span>
                        <span className="text-cyber-purple glow-purple font-display text-lg font-bold">
                            {user.balance}<span className="text-cyber-dim text-xs ml-1">¢</span>
                        </span>
                    </div>
                    <div className="flex items-center gap-2 mt-3 pt-3 border-t border-cyber-border/40 flex-wrap">
                        <button onClick={() => navigate('/game')} className="neon-btn text-xs px-3 sm:px-4 py-1.5 sm:py-2">
                            {t('common.back')}
                        </button>
                        {profile?.user?.isAdmin && (
                            <button onClick={() => navigate('/admin')} className="neon-btn neon-btn-purple text-xs px-3 sm:px-4 py-1.5 sm:py-2">
                                {t('common.admin')}
                            </button>
                        )}
                        {onLogout && (
                            <button onClick={onLogout} className="neon-btn neon-btn-pink text-xs px-3 sm:px-4 py-1.5 sm:py-2 ml-auto">
                                {t('common.logout')}
                            </button>
                        )}
                    </div>
                </header>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
                    {/* Left column — operations */}
                    <div className="lg:col-span-2 space-y-4 sm:space-y-6">
                        <section className="neon-panel p-3 sm:p-6">
                            <h2 className="text-base sm:text-xl font-display font-bold text-white glow-cyan mb-2">{t('profile.topUpBalance')}</h2>
                            <p className="text-xs text-cyber-dim tracking-widest mb-3 sm:mb-4">
                                {t('profile.topUpDescription', { amount: minDeposit })}
                            </p>
                            <div className="flex flex-col md:flex-row gap-3 items-start md:items-end">
                                <div className="flex-1 w-full">
                                    <label className="neon-label block mb-2">{t('profile.amountUsdt')}</label>
                                    <input
                                        type="number"
                                        min={minDeposit}
                                        step="1"
                                        value={depositAmount}
                                        onChange={(e) => setDepositAmount(e.target.value)}
                                        placeholder={t('profile.minPlaceholder', { amount: minDeposit })}
                                        className="neon-input w-full"
                                    />
                                </div>
                                <div className="flex-1 w-full">
                                    <label className="neon-label block mb-2">{t('profile.youWillReceive')}</label>
                                    <div className="neon-input w-full font-mono text-cyber-purple glow-purple">
                                        {parseFloat(depositAmount) >= minDeposit
                                            ? `${Math.floor(parseFloat(depositAmount))} ¢`
                                            : t('profile.enterAmount')}
                                    </div>
                                </div>
                                <button
                                    onClick={submitDeposit}
                                    disabled={submittingDeposit || !canDeposit}
                                    className="neon-btn h-[46px] w-full md:w-auto"
                                >
                                    {submittingDeposit ? t('profile.processing') : t('profile.topUp')}
                                </button>
                            </div>
                        </section>

                        <section className="neon-panel p-3 sm:p-6">
                            <h2 className="text-base sm:text-xl font-display font-bold text-white glow-cyan mb-2">{t('profile.usdtWallet')}</h2>
                            <p className="text-xs text-cyber-dim tracking-widest mb-3 sm:mb-4">
                                {t('profile.walletDescription')}
                            </p>
                            <div className="flex flex-col md:flex-row gap-3">
                                <input
                                    type="text"
                                    value={walletInput}
                                    onChange={(e) => setWalletInput(e.target.value)}
                                    placeholder="TXYZabc1234567890..."
                                    className="neon-input flex-1 font-mono text-sm w-full"
                                />
                                <button
                                    onClick={saveWallet}
                                    disabled={savingWallet || !walletValid || walletInput.trim() === (user.wallet || '')}
                                    className="neon-btn neon-btn-purple w-full md:w-auto"
                                >
                                    {savingWallet ? t('profile.saving') : t('profile.saveWallet')}
                                </button>
                            </div>
                            {walletInput && !walletValid && (
                                <div className="mt-2 text-xs text-cyber-pink glow-pink tracking-widest">
                                    {t('profile.invalidTrc20')}
                                </div>
                            )}
                        </section>

                        <section className="neon-panel p-3 sm:p-6">
                            <h2 className="text-base sm:text-xl font-display font-bold text-white glow-cyan mb-2">{t('profile.withdrawCredits')}</h2>
                            <p className="text-xs text-cyber-dim tracking-widest mb-3 sm:mb-4">
                                {t('profile.withdrawDescriptionAuto', { fee: networkFeeHint })}
                            </p>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 items-end">
                                <div>
                                    <label className="neon-label block mb-2">{t('profile.amountCredits')}</label>
                                    <input
                                        type="number"
                                        min={minWithdrawal}
                                        max={user.balance}
                                        value={amount}
                                        onChange={(e) => setAmount(e.target.value)}
                                        placeholder={t('profile.minPlaceholder', { amount: minWithdrawal })}
                                        className="neon-input w-full"
                                    />
                                </div>
                                <div>
                                    <label className="neon-label block mb-2">{t('profile.youWillReceiveApprox')}</label>
                                    <div className="neon-input w-full font-mono text-cyber-purple glow-purple">
                                        {parseInt(amount) >= minWithdrawal
                                            ? `~${Math.max(0, parseInt(amount) - networkFeeHint)} USDT`
                                            : t('profile.enterAmount')}
                                    </div>
                                </div>
                            </div>
                            <div className="mt-3">
                                <label className="neon-label block mb-2">{t('profile.linkedWallet')}</label>
                                <div className="neon-input w-full font-mono text-cyber-dim truncate text-sm">
                                    {user.wallet || t('profile.notLinkedShort')}
                                </div>
                            </div>
                            <button
                                onClick={submitWithdrawal}
                                disabled={submittingWithdrawal || !canWithdraw}
                                className="neon-btn neon-btn-purple h-[46px] w-full mt-3 whitespace-nowrap"
                            >
                                {submittingWithdrawal ? t('profile.sending') : t('profile.createRequest')}
                            </button>
                            {!hasWallet && (
                                <div className="mt-3 text-xs text-cyber-pink glow-pink tracking-widest">
                                    {t('profile.linkWalletFirst')}
                                </div>
                            )}
                        </section>
                    </div>

                    {/* Right column — overview / stats */}
                    <aside className="space-y-4 sm:space-y-6 lg:sticky lg:top-4 lg:self-start">
                        <section className="neon-panel p-3 sm:p-5">
                            <h2 className="text-xs sm:text-sm font-display font-bold text-white glow-cyan mb-3 tracking-widest">{t('profile.overview')}</h2>
                            <div className="space-y-3">
                                <div>
                                    <div className="neon-label">{t('profile.credits')}</div>
                                    <div className="text-cyber-purple glow-purple font-display text-2xl font-bold">
                                        {user.balance}<span className="text-cyber-dim text-base ml-1">¢</span>
                                    </div>
                                </div>
                                <div>
                                    <div className="neon-label">{t('profile.walletStatus')}</div>
                                    <div className={`font-display tracking-wider ${hasWallet ? 'text-cyber-cyan glow-cyan' : 'text-cyber-pink glow-pink'}`}>
                                        {hasWallet ? t('profile.linked') : t('profile.notLinked')}
                                    </div>
                                </div>
                                <div className="grid grid-cols-2 gap-3 pt-2 border-t border-cyber-border/30">
                                    <div>
                                        <div className="neon-label">{t('profile.minWithdrawal')}</div>
                                        <div className="text-cyber-text font-mono">{minWithdrawal} ¢</div>
                                    </div>
                                    <div>
                                        <div className="neon-label">{t('profile.networkFee')}</div>
                                        <div className="text-cyber-text font-mono">~{networkFeeHint} USDT</div>
                                    </div>
                                </div>
                                {active.length > 0 && (
                                    <div className="pt-2 border-t border-cyber-border/30">
                                        <div className="neon-label">{t('profile.activeNow')}</div>
                                        <div className="text-cyber-purple glow-purple font-display tracking-wider">
                                            {t('profile.pending', { count: active.length })}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </section>

                        <section className="neon-panel p-3 sm:p-5">
                            <h2 className="text-xs sm:text-sm font-display font-bold text-white glow-cyan mb-3 tracking-widest">{t('profile.operatorData')}</h2>
                            <div className="space-y-2.5 text-sm">
                                <div>
                                    <div className="neon-label">{t('profile.handle')}</div>
                                    <div className="text-cyber-cyan glow-cyan font-display tracking-wider">{user.name}</div>
                                </div>
                                <div>
                                    <div className="neon-label">{t('profile.email')}</div>
                                    <div className="text-cyber-text font-mono truncate" title={user.email}>{user.email}</div>
                                </div>
                                <div>
                                    <div className="neon-label">{t('profile.memberSince')}</div>
                                    <div className="text-cyber-text font-mono text-xs">{formatDate(user.createdAt)}</div>
                                </div>
                            </div>
                        </section>

                        {/* Mobile-only language + theme switchers (floating switcher is desktop-only) */}
                        <section className="neon-panel p-3 sm:p-5 md:hidden">
                            <h2 className="text-xs sm:text-sm font-display font-bold text-white glow-cyan mb-3 tracking-widest">{t('profile.appearance')}</h2>
                            <div className="flex items-center gap-3">
                                <LanguageSwitcher variant="dropdown-up-md" />
                                <ThemeSwitcher placement="top" size="md" />
                            </div>
                        </section>
                    </aside>
                </div>

                {/* History tabs */}
                <section className="neon-panel p-3 sm:p-6">
                    <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                        <h2 className="text-base sm:text-xl font-display font-bold text-white glow-cyan">{t('profile.historyTitle')}</h2>
                    </div>
                    <div className="flex flex-wrap gap-1.5 sm:gap-2 mb-4 sm:mb-5 border-b border-cyber-border/40 pb-3">
                        {historyTabs.map((tab) => (
                            <button
                                key={tab.key}
                                onClick={() => setHistoryTab(tab.key)}
                                className={`neon-btn text-[10px] sm:text-xs px-2.5 sm:px-3 py-1.5 ${historyTab === tab.key ? 'neon-btn-purple' : ''}`}
                            >
                                {tab.label}
                                {typeof tab.count === 'number' && tab.count > 0 && ` [${tab.count}]`}
                            </button>
                        ))}
                    </div>

                    {historyTab === 'active' && (
                        <WithdrawalsTable
                            rows={active}
                            empty={t('profile.noActiveRequests')}
                            onCancel={cancelWithdrawal}
                            cancellingId={cancellingId}
                            t={t}
                        />
                    )}
                    {historyTab === 'withdrawals' && (
                        <WithdrawalsTable rows={history} empty={t('profile.noHistoryYet')} showProcessed t={t} />
                    )}
                    {historyTab === 'deposits' && (
                        deposits.length > 0
                            ? <DepositsTable rows={deposits} t={t} />
                            : <div className="text-center py-10 text-cyber-dim font-display tracking-widest text-sm">{t('profile.noDepositsYet')}</div>
                    )}
                    {historyTab === 'games' && <GamesHistory />}
                </section>
            </div>
        </div>
    );
}

const buildDepositStatusLabel = (t) => ({
    waiting: { text: t('profile.status.waiting'), className: 'text-cyber-dim' },
    confirming: { text: t('profile.status.confirming'), className: 'text-cyber-purple glow-purple' },
    confirmed: { text: t('profile.status.confirmed'), className: 'text-cyber-purple glow-purple' },
    sending: { text: t('profile.status.sending'), className: 'text-cyber-purple glow-purple' },
    partially_paid: { text: t('profile.status.partial'), className: 'text-cyber-pink glow-pink' },
    finished: { text: t('profile.status.completed'), className: 'text-cyber-cyan glow-cyan' },
    failed: { text: t('profile.status.failed'), className: 'text-cyber-pink glow-pink' },
    refunded: { text: t('profile.status.refunded'), className: 'text-cyber-pink glow-pink' },
    expired: { text: t('profile.status.expired'), className: 'text-cyber-pink glow-pink' },
});

function DepositsTable({ rows, t }) {
    if (rows.length === 0) return null;
    const depositStatusLabel = buildDepositStatusLabel(t);
    return (
        <div className="overflow-x-auto -mx-3 px-3 sm:mx-0 sm:px-0">
            <table className="w-full text-xs sm:text-sm min-w-[400px]">
                <thead>
                    <tr className="text-left text-cyber-dim tracking-widest text-[10px] sm:text-xs uppercase">
                        <th className="py-2 pr-2 sm:pr-4">{t('profile.table.id')}</th>
                        <th className="py-2 pr-2 sm:pr-4">{t('profile.table.usdt')}</th>
                        <th className="py-2 pr-2 sm:pr-4">{t('profile.table.credits')}</th>
                        <th className="py-2 pr-2 sm:pr-4">{t('profile.table.status')}</th>
                        <th className="py-2 pr-2 sm:pr-4">{t('profile.table.created')}</th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map((row) => {
                        const s = depositStatusLabel[row.status] || depositStatusLabel.waiting;
                        return (
                            <tr key={row.id} className="border-t border-cyber-border/40">
                                <td className="py-2 pr-2 sm:pr-4 font-mono text-cyber-dim">#{row.id}</td>
                                <td className="py-2 pr-2 sm:pr-4 font-display text-cyber-cyan glow-cyan">{row.amountUsd}</td>
                                <td className="py-2 pr-2 sm:pr-4 font-display text-cyber-purple glow-purple">{row.amount}¢</td>
                                <td className={`py-2 pr-2 sm:pr-4 font-display tracking-wider ${s.className}`}>{s.text}</td>
                                <td className="py-2 pr-2 sm:pr-4 font-mono text-cyber-dim text-[10px] sm:text-xs">{formatDate(row.createdAt)}</td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}

function WithdrawalsTable({ rows, empty, showProcessed, onCancel, cancellingId, t }) {
    const statusLabel = buildStatusLabel(t);
    if (rows.length === 0) {
        return (
            <div className="text-center py-10 text-cyber-dim">
                <div className="font-display tracking-widest text-sm">{empty}</div>
            </div>
        );
    }
    return (
        <div className="overflow-x-auto -mx-3 px-3 sm:mx-0 sm:px-0">
            <table className="w-full text-xs sm:text-sm min-w-[420px]">
                <thead>
                    <tr className="text-left text-cyber-dim tracking-widest text-[10px] sm:text-xs uppercase">
                        <th className="py-2 pr-2 sm:pr-4">{t('profile.table.id')}</th>
                        <th className="py-2 pr-2 sm:pr-4">{t('profile.table.amount')}</th>
                        <th className="py-2 pr-2 sm:pr-4">{t('profile.table.wallet')}</th>
                        <th className="py-2 pr-2 sm:pr-4">{t('profile.table.status')}</th>
                        <th className="py-2 pr-2 sm:pr-4">{t('profile.table.created')}</th>
                        {showProcessed && <th className="py-2 pr-2 sm:pr-4">{t('profile.table.processed')}</th>}
                        {onCancel && <th className="py-2 pr-2 sm:pr-4"></th>}
                    </tr>
                </thead>
                <tbody>
                    {rows.map((row) => {
                        const s = statusLabel[row.status] || statusLabel.pending;
                        const isCancellable = row.status === 'pending';
                        const colSpan = (showProcessed ? 6 : 5) + (onCancel ? 1 : 0);
                        return (
                            <React.Fragment key={row.id}>
                                <tr className="border-t border-cyber-border/40">
                                    <td className="py-2 pr-2 sm:pr-4 font-mono text-cyber-dim">#{row.id}</td>
                                    <td className="py-2 pr-2 sm:pr-4 font-display text-cyber-purple glow-purple">{row.amount}¢</td>
                                    <td className="py-2 pr-2 sm:pr-4 font-mono text-cyber-text text-[10px] sm:text-xs" title={row.wallet}>
                                        {shortenWallet(row.wallet)}
                                    </td>
                                    <td className={`py-2 pr-2 sm:pr-4 font-display tracking-wider ${s.className}`}>{s.text}</td>
                                    <td className="py-2 pr-2 sm:pr-4 font-mono text-cyber-dim text-[10px] sm:text-xs">{formatDate(row.createdAt)}</td>
                                    {showProcessed && (
                                        <td className="py-2 pr-2 sm:pr-4 font-mono text-cyber-dim text-[10px] sm:text-xs">{formatDate(row.processedAt)}</td>
                                    )}
                                    {onCancel && (
                                        <td className="py-2 pr-2 sm:pr-4">
                                            {isCancellable && (
                                                <button
                                                    onClick={() => onCancel(row.id)}
                                                    disabled={cancellingId === row.id}
                                                    className="neon-btn neon-btn-pink text-[10px] px-2 sm:px-3 py-1"
                                                >
                                                    {cancellingId === row.id ? '...' : t('common.cancel')}
                                                </button>
                                            )}
                                        </td>
                                    )}
                                </tr>
                                {row.failureReason && (
                                    <tr className="border-t border-cyber-border/20">
                                        <td colSpan={colSpan} className="py-1 pr-2 sm:pr-4 pl-2 sm:pl-4 text-[10px] sm:text-xs text-cyber-pink">
                                            <span className="neon-label mr-2">{t('profile.failureLabel')}</span>
                                            <span className="font-mono">{row.failureReason}</span>
                                        </td>
                                    </tr>
                                )}
                                {row.adminComment && (
                                    <tr className="border-t border-cyber-border/20">
                                        <td colSpan={colSpan} className="py-1 pr-2 sm:pr-4 pl-2 sm:pl-4 text-[10px] sm:text-xs text-cyber-dim">
                                            <span className="neon-label mr-2">{t('profile.adminLabel')}</span>
                                            <span className="text-cyber-text font-mono">{row.adminComment}</span>
                                        </td>
                                    </tr>
                                )}
                            </React.Fragment>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}

export default Profile;
